import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { MDM_TYPES } from '@/constants/mdm';
import { useSourcesStore } from '@/stores';
import type { FilterFieldOption } from '@/types/filters';

export type FieldOptionWithSource = FilterFieldOption<number> & {
  sourceId?: number;
};

type GroupedOption = {
  label: string;
  options: FieldOptionWithSource[];
};

/**
 * Loads filterable field options for a feed, grouped by table, for use in
 * PartnerMappingSingleFilter dropdown and filter row.
 * Excludes deal_contact_role sources (e.g. OpportunityContactRole) to match
 * the population modal's filterable sources list.
 */
export function usePartnerMappingFieldOptions(
  feedId: () => number | undefined,
) {
  const sourcesStore = useSourcesStore();
  const fieldOptions = ref<FieldOptionWithSource[]>([]);
  const groupedFieldOptions = ref<GroupedOption[]>([]);
  const isLoadingOptions = ref(false);

  function loadFieldOptions() {
    const id = feedId();
    if (!id) {
      fieldOptions.value = [];
      groupedFieldOptions.value = [];
      return;
    }

    isLoadingOptions.value = true;
    try {
      const feedSources = sourcesStore
        .getSourcesByFeedId(id)
        .filter((source) => source.mdm_type !== MDM_TYPES.DEAL_CONTACT);
      if (feedSources.length === 0) {
        fieldOptions.value = [];
        groupedFieldOptions.value = [];
        return;
      }

      const fieldsByTable = new Map<
        string,
        { sourceId: number; fields: FilterFieldOption<number>[] }
      >();

      for (const source of feedSources) {
        if (!source.fields || !source.table || source.id == null) continue;

        const filteredFields = source.fields
          .filter((field) => {
            const hasId = field.id != null;
            const notInternal = !field.column?.startsWith('_sdc_');
            const isVisible = field.is_visible !== false;
            const isFilterable = field.is_filterable !== false;
            const notDisabled = !field.syncing_disabled_reason;
            return (
              hasId && notInternal && isVisible && isFilterable && notDisabled
            );
          })
          .map((field) => {
            const fieldId = field.id as number;
            return {
              label: field.display_name || field.nickname || field.column,
              value: fieldId,
              dataType: (field.data_type ??
                'string') as FilterFieldOption<number>['dataType'],
              options: field.options
                ? Object.entries(field.options).map(([k, v]) => ({
                    label: String(v),
                    value: k,
                  }))
                : undefined,
              fetchOptions:
                field.data_type === 'string'
                  ? async (query: string) => {
                      const { data } = await crossbeamApi.POST(
                        '/v0.1/search/source-data',
                        {
                          body: {
                            field: fieldId,
                            query: query || null,
                          },
                        },
                      );
                      return (data || []).map((item: string) => ({
                        label: item,
                        value: item,
                      }));
                    }
                  : undefined,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        if (filteredFields.length > 0) {
          const sourceId = source.id as number;
          const existing = fieldsByTable.get(source.table) ?? {
            sourceId,
            fields: [],
          };
          fieldsByTable.set(source.table, {
            sourceId,
            fields: [...existing.fields, ...filteredFields],
          });
        }
      }

      fieldOptions.value = Array.from(fieldsByTable.values()).flatMap((group) =>
        group.fields.map((field) => ({ ...field, sourceId: group.sourceId })),
      );

      groupedFieldOptions.value = Array.from(fieldsByTable.entries())
        .map(([tableName, group]) => ({
          label: tableName,
          options: group.fields.map((field) => ({
            ...field,
            sourceId: group.sourceId,
          })),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    } finally {
      isLoadingOptions.value = false;
    }
  }

  return {
    fieldOptions,
    groupedFieldOptions,
    isLoadingOptions,
    loadFieldOptions,
  };
}
